// GLSL sources for the editor. A separable blur produces a blurred copy used by
// clarity / texture / sharpen / dehaze, then the main pass applies every global
// adjustment. Coordinates are normalised; geometry (crop, straighten, flips,
// perspective, lens distortion) is applied by warping the sample UV.

export const VERT = /* glsl */ `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

// Separable Gaussian blur (9 taps). u_dir is (texelSize, 0) or (0, texelSize).
export const BLUR_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;
uniform sampler2D u_tex;
uniform vec2 u_dir;
void main() {
  float w[5];
  w[0] = 0.227027; w[1] = 0.194594; w[2] = 0.121621;
  w[3] = 0.054054; w[4] = 0.016216;
  vec3 c = texture(u_tex, v_uv).rgb * w[0];
  for (int i = 1; i < 5; i++) {
    vec2 off = u_dir * float(i);
    c += texture(u_tex, v_uv + off).rgb * w[i];
    c += texture(u_tex, v_uv - off).rgb * w[i];
  }
  outColor = vec4(c, 1.0);
}`;

export const MAIN_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;

uniform sampler2D u_tex;
uniform sampler2D u_blur;

// White balance & tone
uniform float u_temp, u_tint, u_exposure, u_contrast;
uniform float u_highlights, u_shadows, u_whites, u_blacks;
// Color
uniform float u_vibrance, u_saturation;
uniform float u_hslHue[8], u_hslSat[8], u_hslLum[8];
// Color grading (rgb offsets per range + blending/balance)
uniform vec3 u_gradeShadow, u_gradeMid, u_gradeHigh;
uniform float u_gradeBalance;
// Detail
uniform float u_texture, u_clarity, u_dehaze, u_sharpen;
// Geometry
uniform vec4 u_crop;          // x, y, w, h (normalised)
uniform float u_straighten;   // radians
uniform vec2 u_flip;          // 1 or -1
uniform float u_distortion;   // lens
uniform vec2 u_perspective;   // vertical, horizontal
uniform float u_vignette, u_vignetteMid;

const float PI = 3.14159265;

float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

vec3 rgb2hsl(vec3 c) {
  float mx = max(max(c.r, c.g), c.b);
  float mn = min(min(c.r, c.g), c.b);
  float h = 0.0, s = 0.0, l = (mx + mn) * 0.5;
  float d = mx - mn;
  if (d > 1e-5) {
    s = l > 0.5 ? d / (2.0 - mx - mn) : d / (mx + mn);
    if (mx == c.r) h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
    else if (mx == c.g) h = (c.b - c.r) / d + 2.0;
    else h = (c.r - c.g) / d + 4.0;
    h /= 6.0;
  }
  return vec3(h, s, l);
}

float hue2rgb(float p, float q, float t) {
  if (t < 0.0) t += 1.0;
  if (t > 1.0) t -= 1.0;
  if (t < 1.0/6.0) return p + (q - p) * 6.0 * t;
  if (t < 1.0/2.0) return q;
  if (t < 2.0/3.0) return p + (q - p) * (2.0/3.0 - t) * 6.0;
  return p;
}

vec3 hsl2rgb(vec3 hsl) {
  float h = hsl.x, s = hsl.y, l = hsl.z;
  if (s <= 0.0) return vec3(l);
  float q = l < 0.5 ? l * (1.0 + s) : l + s - l * s;
  float p = 2.0 * l - q;
  return vec3(
    hue2rgb(p, q, h + 1.0/3.0),
    hue2rgb(p, q, h),
    hue2rgb(p, q, h - 1.0/3.0)
  );
}

// Weight of a hue (0..1) for one of 8 color bands centred every 45deg.
float bandWeight(float hue, int band) {
  float center = float(band) / 8.0;
  float d = abs(hue - center);
  d = min(d, 1.0 - d);           // wrap-around distance
  float w = 1.0 - smoothstep(0.0, 1.0/8.0, d);
  return w;
}

vec2 applyGeometry(vec2 uv) {
  // Map crop window to full 0..1.
  vec2 p = u_crop.xy + uv * u_crop.zw;
  // Center for rotation / perspective.
  vec2 c = p - 0.5;
  c *= u_flip;
  // Straighten rotation.
  float s = sin(u_straighten), co = cos(u_straighten);
  c = vec2(c.x * co - c.y * s, c.x * s + c.y * co);
  // Perspective (simple keystone).
  c.x *= 1.0 + u_perspective.x * c.y * 0.5;
  c.y *= 1.0 + u_perspective.y * c.x * 0.5;
  // Lens distortion (barrel/pincushion).
  float r2 = dot(c, c);
  c *= 1.0 + u_distortion * r2;
  return c + 0.5;
}

vec3 applyTone(vec3 col) {
  // Exposure (EV).
  col *= pow(2.0, u_exposure);

  // White balance: temp shifts R vs B, tint shifts G.
  col.r *= 1.0 + u_temp * 0.4;
  col.b *= 1.0 - u_temp * 0.4;
  col.g *= 1.0 + u_tint * 0.2;

  // Whites / blacks (endpoint stretch).
  col = (col - u_blacks * 0.15) * (1.0 + u_whites * 0.15);

  // Highlights / shadows (luma-weighted lift/reduce).
  float l = luma(col);
  float hi = smoothstep(0.5, 1.0, l);
  float sh = 1.0 - smoothstep(0.0, 0.5, l);
  col += u_highlights * 0.2 * hi;
  col += u_shadows * 0.25 * sh;

  // Contrast around mid grey.
  col = (col - 0.5) * (1.0 + u_contrast) + 0.5;
  return col;
}

vec3 applyColor(vec3 col) {
  vec3 hsl = rgb2hsl(clamp(col, 0.0, 1.0));

  // Vibrance (protect already-saturated + skin) and saturation.
  float sat = hsl.y;
  hsl.y += u_vibrance * (1.0 - sat) * 0.5;
  hsl.y += u_saturation * 0.5;

  // Per-band HSL.
  for (int i = 0; i < 8; i++) {
    float w = bandWeight(hsl.x, i);
    if (w > 0.0) {
      hsl.x += u_hslHue[i] * 0.05 * w;
      hsl.y += u_hslSat[i] * 0.6 * w;
      hsl.z += u_hslLum[i] * 0.3 * w;
    }
  }
  hsl.y = clamp(hsl.y, 0.0, 1.0);
  hsl.z = clamp(hsl.z, 0.0, 1.0);
  return hsl2rgb(hsl);
}

vec3 applyGrading(vec3 col) {
  float l = luma(col);
  float sh = 1.0 - smoothstep(0.0, 0.5 - u_gradeBalance * 0.3, l);
  float hi = smoothstep(0.5 + u_gradeBalance * 0.3, 1.0, l);
  float mi = 1.0 - sh - hi;
  col += u_gradeShadow * sh;
  col += u_gradeMid * mi;
  col += u_gradeHigh * hi;
  return col;
}

void main() {
  vec2 uv = applyGeometry(v_uv);
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    outColor = vec4(0.06, 0.06, 0.07, 1.0);
    return;
  }

  vec3 col = texture(u_tex, uv).rgb;
  vec3 blur = texture(u_blur, uv).rgb;

  // Detail via local contrast (col - blur).
  vec3 detail = col - blur;
  col += detail * u_clarity * 1.0;      // clarity: midtone local contrast
  col += detail * u_texture * 0.8;      // texture: fine detail
  col += detail * u_sharpen * 1.5;      // unsharp mask sharpening

  // Dehaze: boost contrast + saturation in hazy (low-contrast) areas.
  if (abs(u_dehaze) > 0.001) {
    float l = luma(col);
    col = (col - l) * (1.0 + u_dehaze * 0.5) + l; // local saturation
    col = (col - 0.5) * (1.0 + u_dehaze * 0.4) + 0.5;
  }

  col = applyTone(col);
  col = clamp(col, 0.0, 1.0);
  col = applyColor(col);
  col = applyGrading(col);

  // Vignette.
  if (abs(u_vignette) > 0.001) {
    vec2 d = v_uv - 0.5;
    float r = length(d) * 1.41421;
    float mid = mix(0.2, 1.0, u_vignetteMid);
    float v = smoothstep(mid, mid - 0.5, r);
    col *= 1.0 + u_vignette * (1.0 - v);
  }

  outColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;
